'use client';

import { c as _c } from "react/compiler-runtime";
import { Renown as RenownSDK } from '@powerhousedao/reactor-browser';
import { type ReactNode, useState } from 'react';
import { createClient } from '../client/create-client';
import { RenownAuthBridge } from './bridge';
import { PowerhouseClientProvider } from './client-context';
export interface PowerhouseSDKProviderProps {
  appName: string;
  /**
   * Reactor GraphQL endpoint. The host app reads its own env (e.g.
   * `NEXT_PUBLIC_SWITCHBOARD_URL`) and passes it in — the package never reads
   * `process.env`. Defaults to the local switchboard when omitted.
   */
  switchboardUrl?: string;
  renownUrl?: string;
  /**
   * When false, Renown is not mounted and `useAuth()` returns
   * `'unauthenticated'` permanently. The client is still created and provided,
   * so non-auth reads via `useClient()` keep working. Useful for
   * feature-flagging auth off in lower environments.
   */
  enabled?: boolean;
  /**
   * Side-effect hook for app-level reactions to auth errors (toast,
   * telemetry). The SDK itself does not toast, redirect, or log on errors
   * — that's the app's policy. Called with the original error from
   * Renown's `onError`.
   */
  onAuthError?: (error: unknown) => void;
  children: ReactNode;
}

/**
 * Single mount point for everything the signed-document SDK needs:
 *
 *  - creates ONE {@link PowerhouseClient} and provides it via context
 *    (read with `useClient()`);
 *  - mounts Renown (the Powerhouse identity SDK);
 *  - bridges Renown's bearer token into THIS client instance's token provider.
 *
 * The client lives in `useState` (not `useMemo`, which React may discard) so
 * the token provider the bridge installs survives re-renders. The client and
 * its context wrap children regardless of `enabled`; only Renown + the bridge
 * are gated, so reads work even with auth flagged off.
 */
export function PowerhouseSDKProvider(t0) {
  const $ = _c(12);
  const {
    appName,
    switchboardUrl,
    renownUrl,
    enabled: t1,
    onAuthError,
    children
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== switchboardUrl) {
    t2 = () => createClient({
      switchboardUrl
    });
    $[0] = switchboardUrl;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [client] = useState(t2);
  let t3;
  if ($[2] !== appName || $[3] !== client || $[4] !== enabled || $[5] !== onAuthError || $[6] !== renownUrl) {
    t3 = enabled ? <><RenownSDK appName={appName} url={renownUrl} onError={onAuthError} /><RenownAuthBridge client={client} /></> : null;
    $[2] = appName;
    $[3] = client;
    $[4] = enabled;
    $[5] = onAuthError;
    $[6] = renownUrl;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== children || $[9] !== client || $[10] !== t3) {
    t4 = <PowerhouseClientProvider client={client}>{t3}{children}</PowerhouseClientProvider>;
    $[8] = children;
    $[9] = client;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}